import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import Logo from "@/components/Logo";
import { Check, Loader2, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Phase 4.7 — 3 fixed pricing packages. NO Free Check card. NO uSeen Assist
// card. NO "show more plans" reveal. Free visibility check exists as a
// separate CTA block above the grid.
//
// Real payment / Stripe / billing logic is intentionally NOT touched.
// Plan select still calls the existing /purchase-requests endpoint just
// like Phase 4, so backend remains the source of truth for plan names.

const COPY = {
  tr: {
    eyebrow: "Fiyatlandırma",
    title: "Size uygun planı seçin",
    subtitle: "Her plan AI görünürlük analizi, Yusi Aksiyonları, hazır çıktılar ve onay akışı içerir.",
    notice: "Şu an ödeme alınmıyor — Plan Seç purchase request oluşturur.",
    freeBlock: {
      title: "Önce ücretsiz görünürlük kontrolü yapın",
      desc: "İşletmenizin AI görünürlük skorunu görün, ilk eksikleri keşfedin ve Yusi'nin sizin için nasıl aksiyonlar hazırladığını deneyimleyin.",
      cta: "Ücretsiz Görünürlük Kontrolü Yap",
    },
    perMonth: "/ay",
    featuresHeading: "Plana dahil",
    limitsHeading: "Bu pakette yok",
    customNote: "Çoklu şube veya özel kullanım için bizimle iletişime geçebilirsiniz.",
    customCta: "İletişime geç",
    customEmail: "contact@useen.ai",
    mostPopular: "En Çok Tercih Edilen",
    done: "Talep Alındı ✓",
    backToApp: "Panele Dön",
    login: "Giriş Yap",
    toastOk: "Talebiniz alındı. uSeen ekibi sizinle iletişime geçecek.",
    toastErr: "Talep gönderilemedi",
  },
  en: {
    eyebrow: "Pricing",
    title: "Choose your plan",
    subtitle: "Every plan includes AI visibility analysis, Yusi Actions, prepared outputs and the approval workflow.",
    notice: "No payment is collected yet — Choose plan creates a purchase request.",
    freeBlock: {
      title: "Start with a free visibility check",
      desc: "See your AI visibility score, discover the first gaps, and experience how Yusi prepares actions for your business.",
      cta: "Run my free visibility check",
    },
    perMonth: "/ month",
    featuresHeading: "Included",
    limitsHeading: "Not in this package",
    customNote: "For larger teams, agencies or custom usage, contact us.",
    customCta: "Get in touch",
    customEmail: "contact@useen.ai",
    mostPopular: "Most Popular",
    done: "Request received ✓",
    backToApp: "Back to app",
    login: "Log in",
    toastOk: "Got it. The uSeen team will reach out shortly.",
    toastErr: "Could not send your request.",
  },
};

const PLANS = {
  tr: [
    {
      id: "starter",
      name: "Başlangıç",
      price: "₺790",
      subtitle: "AI görünürlüğüne başlamak isteyen tek işletmeler için.",
      cta: "Başlangıç paketini seç",
      features: [
        "1 işletme",
        "AI görünürlük skoru",
        "Yusi Aksiyonları",
        "Aylık 10 hazır çıktı",
        "Google İşletme açıklaması hazırlama",
        "Hakkımızda metni hazırlama",
        "SSS içeriği hazırlama",
        "Hizmet açıklamaları",
        "Yorum isteme mesajı",
        "Aylık 2 yeniden tarama",
        "Temel rapor",
        "Yusi İzinleri paneli",
      ],
      limitations: [
        "Sınırlı AI soru testi",
        "Sınırlı rakip görünümü",
        "Tek işletme / tek lokasyon",
        "Gelişmiş raporlama yok",
        "Gerçek entegrasyonlar dahil değildir",
      ],
    },
    {
      id: "growth",
      name: "Büyüme",
      price: "₺1.990",
      subtitle: "AI görünürlüğünü düzenli artırmak isteyen işletmeler için.",
      cta: "Büyüme paketini seç",
      featured: true,
      features: [
        "1 işletme",
        "Starter'daki her şey",
        "Aylık 40 hazır çıktı",
        "Haftalık yeniden tarama",
        "AI soru testleri",
        "Rakip karşılaştırması",
        "Onayla / reddet / kullandım akışı",
        "Hazır yerel görünürlük içerikleri",
        "Meta description önerileri",
        "Schema önerileri",
        "PDF rapor",
        "Gelişim takibi",
        "Yusi İzinleri paneli",
      ],
      limitations: [
        "Tek işletme / tek lokasyon",
        "Çoklu lokasyon yönetimi yok",
        "Gerçek entegrasyonlar aktif değildir",
      ],
    },
    {
      id: "pro",
      name: "Profesyonel",
      price: "₺4.990",
      subtitle: "Çoklu lokasyon, ajans ve gelişmiş görünürlük yönetimi için.",
      cta: "Profesyonel paketi seç",
      features: [
        "3 işletme veya lokasyon",
        "Growth'taki her şey",
        "Aylık 150 hazır çıktı",
        "Daha sık yeniden tarama",
        "Gelişmiş AI soru testleri",
        "Gelişmiş rakip takibi",
        "Çoklu lokasyon karşılaştırması",
        "Gelişmiş PDF raporlar",
        "Öncelikli destek",
        "Gelecekteki entegrasyonlara erken erişim hazırlığı",
        "Yusi Permission Center gelişmiş kullanım hazırlığı",
        "Ajans / ekip kullanımına hazırlık",
      ],
      limitations: [
        "Gerçek entegrasyonlar henüz aktif değildir",
        "Otomatik yayınlama henüz aktif değildir",
        "OAuth bağlantıları Phase 5 kapsamındadır",
      ],
    },
  ],
  en: [
    {
      id: "starter",
      name: "Starter",
      price: "₺790",
      subtitle: "For single businesses starting their AI visibility journey.",
      cta: "Choose Starter",
      features: [
        "1 business",
        "AI visibility score",
        "Yusi Actions",
        "10 prepared outputs per month",
        "Google Business description preparation",
        "About Us content preparation",
        "FAQ content preparation",
        "Service descriptions",
        "Review request message",
        "2 rescans per month",
        "Basic report",
        "Yusi Permissions panel",
      ],
      limitations: [
        "Limited AI question tests",
        "Limited competitor view",
        "Single business / single location",
        "No advanced reporting",
        "Real integrations are not included yet",
      ],
    },
    {
      id: "growth",
      name: "Growth",
      price: "₺1,990",
      subtitle: "For businesses that want to improve AI visibility consistently.",
      cta: "Choose Growth",
      featured: true,
      features: [
        "1 business",
        "Everything in Starter",
        "40 prepared outputs per month",
        "Weekly rescan",
        "AI question tests",
        "Competitor comparison",
        "Approve / reject / used workflow",
        "Local visibility content",
        "Meta description suggestions",
        "Schema suggestions",
        "PDF report",
        "Progress tracking",
        "Yusi Permissions panel",
      ],
      limitations: [
        "Single business / single location",
        "No multi-location management",
        "Real integrations are not active yet",
      ],
    },
    {
      id: "pro",
      name: "Pro",
      price: "₺4,990",
      subtitle: "For multi-location businesses, agencies and advanced visibility management.",
      cta: "Choose Pro",
      features: [
        "3 businesses or locations",
        "Everything in Growth",
        "150 prepared outputs per month",
        "More frequent rescans",
        "Advanced AI question tests",
        "Advanced competitor tracking",
        "Multi-location comparison",
        "Advanced PDF reports",
        "Priority support",
        "Future integration readiness",
        "Advanced Yusi Permission Center readiness",
        "Agency / team usage readiness",
      ],
      limitations: [
        "Real integrations are not active yet",
        "Auto-publishing is not active yet",
        "OAuth connections belong to a future phase",
      ],
    },
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  const insideApp = location.pathname.startsWith("/app/");

  const c = COPY[lang] || COPY.tr;
  const plans = PLANS[lang] || PLANS.tr;

  const select = async (plan) => {
    if (!user) {
      navigate("/kayit");
      return;
    }
    setLoadingPlan(plan.id);
    try {
      await api.post("/purchase-requests", { plan: plan.name });
      toast.success(c.toastOk);
      setDoneIds((arr) => [...arr, plan.id]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || c.toastErr);
    } finally {
      setLoadingPlan(null);
    }
  };

  const goFree = () => {
    navigate(user ? "/app" : "/kayit");
  };

  const grid = (
    <>
      <div className={insideApp ? "" : "text-center mb-12"}>
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3">{c.eyebrow}</div>
        <h1 className={`font-display font-bold text-white mb-4 ${insideApp ? "text-4xl" : "text-5xl sm:text-6xl"}`}>
          {c.title}
        </h1>
        <p className={`text-slate-400 text-base sm:text-lg ${insideApp ? "" : "max-w-2xl mx-auto"}`}>
          {c.subtitle}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-xs text-slate-400 glass rounded-full px-3 py-1.5">
          <ShieldCheck size={12} className="text-emerald-400" />
          <span>{c.notice}</span>
        </div>
      </div>

      {/* Free visibility check — separate CTA, NOT a pricing card */}
      <div
        className="glass-strong rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.05] p-5 sm:p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
        data-testid="pricing-free-cta"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="rounded-xl bg-cyan-500/15 border border-cyan-500/30 p-2 shrink-0">
            <Sparkles size={18} className="text-cyan-300" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold text-white">{c.freeBlock.title}</div>
            <p className="text-sm text-slate-300 mt-1 leading-relaxed">{c.freeBlock.desc}</p>
          </div>
        </div>
        <button
          onClick={goFree}
          className="neon-btn rounded-xl px-5 py-3 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 shrink-0"
          data-testid="pricing-free-cta-btn"
        >
          {c.freeBlock.cta}
          <ArrowRight size={14} />
        </button>
      </div>

      {/* 3 paid packages — exactly 3, never more, no hidden reveal */}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const isDone = doneIds.includes(p.id);
          const cardStyle = p.featured
            ? "glass-strong border-2 border-blue-500/50 shadow-[0_0_60px_rgba(59,130,246,0.22)]"
            : "glass border border-white/10";
          const badgeStyle = p.featured
            ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-[0_0_20px_rgba(168,85,247,0.5)]"
            : "";
          const btnStyle = p.featured
            ? "neon-btn text-white"
            : "ghost-btn text-white hover:bg-white/10";
          return (
            <div
              key={p.id}
              className={`rounded-2xl p-7 flex flex-col relative ${cardStyle}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.featured && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full ${badgeStyle}`}
                     data-testid={`plan-badge-${p.id}`}>
                  {c.mostPopular}
                </div>
              )}
              <div className="font-display text-xl font-semibold text-white mb-1" data-testid={`plan-name-${p.id}`}>{p.name}</div>
              <div className="flex items-baseline gap-1 mb-3" data-testid={`plan-price-${p.id}`}>
                <span className="font-mono text-3xl text-white">{p.price}</span>
                <span className="text-xs text-slate-500">{c.perMonth}</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-5 min-h-[48px]">{p.subtitle}</p>

              <div className="text-[10px] uppercase tracking-wider text-cyan-300/80 mb-2">{c.featuresHeading}</div>
              <ul className="space-y-2 mb-5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="text-[13px] text-slate-200 flex gap-2 leading-relaxed">
                    <Check size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{c.limitsHeading}</div>
                <ul className="space-y-1">
                  {p.limitations.map((l) => (
                    <li key={l} className="text-[11px] text-slate-400 leading-relaxed">· {l}</li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => select(p)}
                disabled={loadingPlan === p.id || isDone}
                className={`rounded-xl py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 ${btnStyle}`}
                data-testid={`plan-select-${p.id}`}
              >
                {loadingPlan === p.id && <Loader2 size={14} className="animate-spin" />}
                {isDone ? c.done : p.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* Custom / agency note — NOT a card */}
      <p className="mt-8 text-sm text-slate-400 text-center" data-testid="pricing-custom-note">
        {c.customNote}{" "}
        <a
          href={`mailto:${c.customEmail}`}
          className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
          data-testid="pricing-custom-cta"
        >
          {c.customCta}
        </a>
      </p>
    </>
  );

  if (insideApp) {
    return <div data-testid="pricing-page">{grid}</div>;
  }

  return (
    <div className="min-h-screen relative" data-testid="pricing-page">
      <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
      <header className="px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to={lang === "en" ? "/" : "/tr"}><Logo /></Link>
        <Link to={user ? "/app" : "/giris"} className="text-sm text-slate-300 hover:text-white">
          {user ? c.backToApp : c.login}
        </Link>
      </header>

      <main className="px-6 lg:px-12 py-12 max-w-7xl mx-auto">
        {grid}
      </main>
    </div>
  );
}
